import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiGet, fmtDate, daysUntil, statusLabel } from '../utils/api';

const API = process.env.REACT_APP_API_URL;

/* ── token helper (impersonation-aware) ──────────────────────────────── */
function getToken() {
  return sessionStorage.getItem('awwt_imp_token') || localStorage.getItem('awwt_token');
}

/* ══════════════════════════════════════════════════════════════════════ */
/*  VendorDashboard                                                        */
/* ══════════════════════════════════════════════════════════════════════ */
// filter keys → which profiles each stat card shows
const FILTERS = {
  active:   p => p.status === 'approved',
  pending:  p => ['submitted','under_review'].includes(p.status),
  expiring: p => p.status === 'approved' && daysUntil(p.expires_at) <= 90,
  needs:    p => ['draft','revisions_requested'].includes(p.status),
};

export function VendorDashboard() {
  const [profiles,   setProfiles]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [activeCard, setActiveCard] = useState(null); // which stat card is selected
  const navigate = useNavigate();

  useEffect(() => {
    apiGet('/profiles/my')
      .then(setProfiles)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const active    = profiles.filter(FILTERS.active);
  const pending   = profiles.filter(FILTERS.pending);
  const expiring  = profiles.filter(FILTERS.expiring);
  const needsWork = profiles.filter(FILTERS.needs);

  /* clicking a card toggles it; also clear text search */
  function toggleCard(key) {
    setActiveCard(prev => prev === key ? null : key);
    setSearch('');
  }

  /* apply stat-card filter first, then text search on top */
  let filtered = activeCard ? profiles.filter(FILTERS[activeCard]) : profiles;
  const q = search.toLowerCase().trim();
  if (q) {
    filtered = filtered.filter(p =>
      (p.waste_name     || '').toLowerCase().includes(q) ||
      (p.company_name   || '').toLowerCase().includes(q) ||
      (p.profile_number || '').toLowerCase().includes(q)
    );
  }

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  const cardStyle = (key) => ({
    cursor: 'pointer',
    outline: activeCard === key ? '2px solid var(--forest, #2d6a4f)' : 'none',
    outlineOffset: 2,
    transition: 'outline .15s, opacity .15s',
    opacity: activeCard && activeCard !== key ? 0.55 : 1,
  });

  return (
    <div>
      <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div>
          <h1>Vendor Dashboard</h1>
          <p>Manage your waste profile submissions</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/vendor/new')}>
          + New Profile
        </button>
      </div>

      <div className="stats-grid">
        <div className="stat-card" style={cardStyle('active')} onClick={() => toggleCard('active')}>
          <div className="stat-number">{active.length}</div>
          <div className="stat-label">Active Profiles</div>
        </div>
        <div className="stat-card warn" style={cardStyle('pending')} onClick={() => toggleCard('pending')}>
          <div className="stat-number">{pending.length}</div>
          <div className="stat-label">Under Review</div>
        </div>
        <div className="stat-card warn" style={cardStyle('expiring')} onClick={() => toggleCard('expiring')}>
          <div className="stat-number">{expiring.length}</div>
          <div className="stat-label">Expiring Soon</div>
        </div>
        <div className="stat-card neutral" style={cardStyle('needs')} onClick={() => toggleCard('needs')}>
          <div className="stat-number">{needsWork.length}</div>
          <div className="stat-label">Drafts / Action Needed</div>
        </div>
      </div>

      {needsWork.length > 0 && (
        <div className="alert alert-warn" style={{ marginBottom:20 }}>
          <strong>Action required:</strong> You have {needsWork.length} profile{needsWork.length>1?'s':''} that need{needsWork.length===1?'s':''} attention.
        </div>
      )}
      {expiring.length > 0 && (
        <div className="alert alert-warn" style={{ marginBottom:20 }}>
          <strong>Renewal reminder:</strong> {expiring.length} profile{expiring.length>1?'s are':' is'} expiring within 90 days.
        </div>
      )}

      <div className="card">
        <div className="card-header" style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
          <h2 style={{ margin:0 }}>
            {activeCard === 'active'   && 'Active Profiles'}
            {activeCard === 'pending'  && 'Under Review'}
            {activeCard === 'expiring' && 'Expiring Soon'}
            {activeCard === 'needs'    && 'Drafts / Action Needed'}
            {!activeCard               && 'All Profiles'}
            {activeCard && (
              <button
                onClick={() => setActiveCard(null)}
                style={{ marginLeft:10, fontSize:11, fontWeight:400, background:'var(--border)', border:'none',
                         borderRadius:10, padding:'2px 8px', cursor:'pointer', color:'var(--ink-mid)', verticalAlign:'middle' }}
              >
                ✕ clear
              </button>
            )}
          </h2>
          <input
            type="search"
            placeholder="Search by name, company or profile #…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ padding:'6px 12px', borderRadius:6, border:'1px solid var(--border)', fontSize:13, width:280 }}
          />
        </div>

        {profiles.length === 0 ? (
          <div className="empty-state">
            <div className="icon">📋</div>
            <p>No waste profiles yet.</p>
            <button className="btn btn-primary" style={{ marginTop:16 }} onClick={() => navigate('/vendor/new')}>
              Submit Your First Profile
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <p>No profiles match "<strong>{search}</strong>"</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr>
                <th>Profile #</th>
                <th>Waste Name</th>
                <th>Status</th>
                <th>Submitted</th>
                <th>Expires</th>
                <th></th>
              </tr></thead>
              <tbody>
                {filtered.map(p => {
                  const days = daysUntil(p.expires_at);
                  return (
                    <tr key={p.id}>
                      <td>
                        {p.profile_number
                          ? <span className="mono">{p.profile_number}</span>
                          : <span className="text-muted">—</span>}
                      </td>
                      <td><strong>{p.waste_name}</strong><br /><span className="text-muted">{p.company_name}</span></td>
                      <td>
                        <span className={`badge badge-${p.status}`}>{statusLabel(p.status)}</span>
                        {p.status === 'revisions_requested' && (
                          <div style={{ fontSize:11, color:'#880e4f', marginTop:4 }}>Action required</div>
                        )}
                      </td>
                      <td className="text-muted">{fmtDate(p.submitted_at)}</td>
                      <td>
                        {p.expires_at ? (
                          <span className={days !== null && days <= 30 ? 'expiring-soon' : ''}>
                            {fmtDate(p.expires_at)}
                            {days !== null && days > 0  && <div style={{ fontSize:11, color:'var(--ink-lt)' }}>{days} days</div>}
                            {days !== null && days <= 0 && <div style={{ fontSize:11, color:'#b71c1c' }}>Expired</div>}
                          </span>
                        ) : <span className="text-muted">—</span>}
                      </td>
                      <td>
                        <div className="btn-group">
                          <button className="btn btn-secondary btn-sm"
                            onClick={() => navigate(`/vendor/profiles/${p.id}`)}>
                            View
                          </button>
                          {['draft','revisions_requested'].includes(p.status) && (
                            <button className="btn btn-primary btn-sm"
                              onClick={() => navigate(`/vendor/profiles/${p.id}/edit`)}>
                              Edit
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */
/*  VendorProfileDetail                                                    */
/* ══════════════════════════════════════════════════════════════════════ */
export function VendorProfileDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile,     setProfile]     = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [pdfLoading,  setPdfLoading]  = useState(false);
  const [zipLoading,  setZipLoading]  = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      apiGet(`/profiles/${id}`),
      apiGet(`/attachments/profile/${id}`)
    ])
      .then(([p, atts]) => { setProfile(p); setAttachments(atts || []); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  /* ── Download profile as PDF (same as admin) ───────────────────────── */
  const downloadPDF = async () => {
    setPdfLoading(true);
    try {
      const res = await fetch(`${API}/api/profiles/${id}/pdf`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (!res.ok) {
        let msg = `Server error ${res.status}`;
        try { const j = await res.json(); msg = j.error || j.message || msg; } catch {}
        alert(`Could not generate PDF: ${msg}`);
        return;
      }
      const ct = res.headers.get('content-type') || '';
      if (!ct.includes('pdf')) {
        // Server returned non-PDF (likely a JSON error with 200 status)
        let msg = 'PDF generation failed on the server.';
        try { const j = await res.json(); msg = j.error || j.message || msg; } catch {}
        alert(msg);
        return;
      }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url;
      a.download = `waste-profile-${profile?.profile_number || id}.pdf`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) { alert(`Failed to download PDF: ${err.message}`); }
    finally { setPdfLoading(false); }
  };

  /* ── Open single attachment inline ─────────────────────────────────── */
  const openAttachment = async (attId) => {
    const res = await fetch(`${API}/api/attachments/${attId}`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    if (!res.ok) { alert('Could not load file.'); return; }
    const blob = await res.blob();
    const url  = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };

  /* ── Force-download single attachment ──────────────────────────────── */
  const downloadAttachment = async (attId, filename) => {
    const res = await fetch(`${API}/api/attachments/${attId}?download=1`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    if (!res.ok) { alert('Could not download file.'); return; }
    const blob = await res.blob();
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = filename || 'attachment';
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  /* ── Download all attachments as ZIP ───────────────────────────────── */
  const downloadAllZip = async () => {
    setZipLoading(true);
    try {
      const res = await fetch(`${API}/api/attachments/profile/${id}/zip`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (!res.ok) { alert('Could not create zip archive.'); return; }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url;
      a.download = `attachments-${profile?.profile_number || id}.zip`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch { alert('Failed to download zip.'); }
    finally { setZipLoading(false); }
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!profile) return <div className="alert alert-error">Profile not found.</div>;

  return (
    <div>
      <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/vendor/profiles')} style={{ marginBottom:8 }}>
            ← Back to Profiles
          </button>
          <h1>{profile.waste_name}</h1>
          <p>
            {profile.profile_number
              ? <span className="mono">{profile.profile_number}</span>
              : 'Profile number pending'}
            {' · '}
            <span className={`badge badge-${profile.status}`}>{statusLabel(profile.status)}</span>
          </p>
        </div>
        <div className="btn-group" style={{ flexWrap:'wrap', justifyContent:'flex-end', gap:8 }}>
          <button
            className="btn btn-secondary btn-sm"
            onClick={downloadPDF}
            disabled={pdfLoading}
            title="Download full profile as PDF"
          >
            {pdfLoading ? '⏳ Generating…' : '⬇ Download Profile PDF'}
          </button>
          {['draft','revisions_requested'].includes(profile.status) && (
            <button className="btn btn-primary" onClick={() => navigate(`/vendor/profiles/${id}/edit`)}>
              Edit Profile
            </button>
          )}
        </div>
      </div>

      {profile.status === 'revisions_requested' && (
        <div className="alert alert-warn">
          <strong>Revisions requested:</strong> {profile.revision_notes}
        </div>
      )}
      {profile.status === 'rejected' && (
        <div className="alert alert-error">
          <strong>Not approved:</strong> {profile.rejection_reason}
        </div>
      )}

      {/* ── Profile Status ─────────────────────────────────────────────── */}
      <div className="card">
        <div className="card-header"><h2>Profile Status</h2></div>
        <div style={{ display:'flex', gap:32, flexWrap:'wrap' }}>
          {[
            ['Status',    <span className={`badge badge-${profile.status}`}>{statusLabel(profile.status)}</span>],
            ['Submitted', fmtDate(profile.submitted_at)],
            ['Approved',  fmtDate(profile.approved_at)],
            ['Expires',   fmtDate(profile.expires_at)],
          ].map(([k,v]) => (
            <div key={k}>
              <div className="text-muted" style={{ marginBottom:4 }}>{k}</div>
              <div style={{ fontWeight:500 }}>{v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Generator Information ──────────────────────────────────────── */}
      <div className="card">
        <div className="card-header"><h2>Generator Information</h2></div>
        <ProfileDetailRows profile={profile} fields={[
          ['Company Name',  'company_name'],
          ['Contact Name',  'contact_name'],
          ['Title',         'contact_title'],
          ['Email',         'contact_email'],
          ['Phone',         'contact_phone'],
          ['Address',       'generator_address'],
          ['City',          'generator_city'],
          ['State',         'generator_state'],
          ['ZIP',           'generator_zip'],
          ['EPA ID',        'epa_id'],
        ]} />
      </div>

      {/* ── Waste Characterization ─────────────────────────────────────── */}
      <div className="card">
        <div className="card-header"><h2>Waste Characterization</h2></div>
        <ProfileDetailRows profile={profile} fields={[
          ['Waste Name',    'waste_name'],
          ['Description',   'waste_description'],
          ['Physical State','physical_state'],
          ['Category',      'waste_category'],
          ['Color',         'color'],
          ['Odor',          'odor'],
          ['pH Range',      null, (profile.ph_min || profile.ph_max) ? `${profile.ph_min || '—'} – ${profile.ph_max || '—'}` : '—'],
          ['Flash Point',   'flash_point_f', profile.flash_point_f ? `${profile.flash_point_f} °F` : '—'],
          ['Hazardous',     null, profile.is_hazardous ? 'Yes' : 'No'],
          ['RCRA Codes',    null, (profile.rcra_codes || []).join(', ') || '—'],
        ]} />
      </div>

      {/* ── Attachments ────────────────────────────────────────────────── */}
      <div className="card">
        <div className="card-header"><h2>Attachments / Lab Reports</h2></div>
        {attachments.length === 0 ? (
          <p className="text-muted" style={{ marginBottom:20 }}>No attachments uploaded for this profile.</p>
        ) : (
          <>
            <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:12 }}>
              {attachments.map(att => (
                <div key={att.id} style={{
                  display:'flex', alignItems:'center', justifyContent:'space-between',
                  padding:'10px 14px', background:'var(--cream)', borderRadius:'var(--radius)',
                  border:'1px solid var(--border)'
                }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <span style={{ fontSize:20 }}>📎</span>
                    <div>
                      <div style={{ fontSize:13, fontWeight:600 }}>{att.original_name}</div>
                      <div style={{ fontSize:11, color:'var(--ink-lt)' }}>
                        {att.mime_type} · {Math.round(att.file_size / 1024)} KB · {fmtDate(att.created_at)}
                      </div>
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:6 }}>
                    <button onClick={() => openAttachment(att.id)} className="btn btn-secondary btn-sm" title="Open in new tab">
                      View
                    </button>
                    <button onClick={() => downloadAttachment(att.id, att.original_name)} className="btn btn-secondary btn-sm" title="Download file">
                      ⬇ Download
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Download all as ZIP */}
            <div style={{
              display:'flex', alignItems:'center', justifyContent:'space-between',
              padding:'10px 14px', background:'#f0f4f0', borderRadius:'var(--radius)',
              border:'1px solid #c8dcc8', marginBottom:20
            }}>
              <div style={{ fontSize:13, color:'var(--ink-mid)' }}>
                <strong>{attachments.length}</strong> file{attachments.length !== 1 ? 's' : ''} attached
              </div>
              <button
                onClick={downloadAllZip}
                disabled={zipLoading}
                className="btn btn-secondary btn-sm"
                style={{ borderColor:'var(--forest, #2d6a4f)', color:'var(--forest, #2d6a4f)' }}
              >
                {zipLoading ? '⏳ Zipping…' : '⬇ Download All (.zip)'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ── shared helper ───────────────────────────────────────────────────── */
function ProfileDetailRows({ profile, fields }) {
  return (
    <div>
      {fields.map(([label, key, override]) => (
        <div key={label} className="detail-row">
          <div className="detail-label">{label}</div>
          <div className="detail-value">{override ?? (key ? profile[key] || '—' : '—')}</div>
        </div>
      ))}
    </div>
  );
}
